import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiAlertCircle, FiCheckCircle, FiArrowRight, FiShield, FiTrendingUp, FiInfo, FiClock, FiCheck, FiBarChart2, FiAlertTriangle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { saveConsultation } from '../services/api';

const Result = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [negotiating, setNegotiating] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const storedResult = sessionStorage.getItem('consultResult');
    if (storedResult) {
      const parsedResult = JSON.parse(storedResult);
      setResult(parsedResult);
      
      if (isAuthenticated) {
        saveConsultation(parsedResult.cpf, parsedResult).catch(console.error);
      }
    } else {
      navigate('/');
    }
    setLoading(false);
  }, [isAuthenticated, navigate]);

  const handleNegotiate = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('pendingNegotiation', JSON.stringify(result));
      navigate('/login');
      return;
    }

    setNegotiating(true);
    setTimeout(() => {
      window.open('https://www.serasa.com.br', '_blank');
      setNegotiating(false);
    }, 1500);
  };

  const getScoreColor = (score) => {
    if (score >= 700) return 'text-success-500';
    if (score >= 500) return 'text-warning-500';
    return 'text-danger-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 700) return 'Bom';
    if (score >= 500) return 'Regular';
    return 'Baixo';
  };

  const getScoreProgress = (score) => {
    return Math.min((score / 1000) * 100, 100);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { score, hasNegative, debts = [], totalAmount = 0, totalDiscount = 0, totalDebt = 0, totalOriginal = 0, documentType = 'CPF' } = result;
  
  const finalTotalAmount = totalAmount || totalDebt;
  
  const totalOriginalAmount = totalOriginal || (debts.reduce((sum, d) => sum + (d.amount || 0), 0));
  const totalFinalAmount = debts.reduce((sum, d) => sum + ((d.amount || 0) * (1 - (d.discount || 0) / 100)), 0);
  const discountPercentage = totalOriginalAmount > 0 ? Math.round((1 - totalFinalAmount / totalOriginalAmount) * 100) : 0;
  
  const documentLabel = documentType === 'CNPJ' ? 'CNPJ' : 'CPF';
  const documentValue = result.cnpj || result.cpf;

  const showDebtsList = hasNegative && debts.length > 0;
  const showCleanName = !hasNegative;

  return (
    <>
      <Helmet>
        <title>Resultado da Consulta - ConsulteSeuNome</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Score Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card mb-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-6 md:mb-0">
                <div className="flex items-center justify-center md:justify-start mb-2">
                  <FiBarChart2 className="w-5 h-5 text-gray-400 mr-2" />
                  <h2 className="text-lg text-gray-500">Seu Score Creditício</h2>
                </div>
                <div className={`text-6xl font-bold ${getScoreColor(score)}`}>
                  {score}
                </div>
                <div className="flex items-center justify-center md:justify-start mt-2 space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(score)} ${score >= 700 ? 'bg-success-100' : score >= 500 ? 'bg-warning-100' : 'bg-danger-100'}`}>
                    {getScoreLabel(score)}
                  </span>
                  <span className="text-sm text-gray-500">de 0 a 1000</span>
                </div>
              </div>
              
              <div className="w-full md:w-64">
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${getScoreProgress(score)}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className={`h-full rounded-full ${
                      score >= 700 ? 'bg-success-500' : score >= 500 ? 'bg-warning-500' : 'bg-danger-500'
                    }`}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>0</span>
                  <span>500</span>
                  <span>1000</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center p-2 bg-danger-50 rounded-lg">
                    <div className="text-danger-500 font-semibold">0-499</div>
                    <div className="text-gray-500">Baixo</div>
                  </div>
                  <div className="text-center p-2 bg-warning-50 rounded-lg">
                    <div className="text-warning-500 font-semibold">500-699</div>
                    <div className="text-gray-500">Regular</div>
                  </div>
                  <div className="text-center p-2 bg-success-50 rounded-lg">
                    <div className="text-success-500 font-semibold">700+</div>
                    <div className="text-gray-500">Bom</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`card mb-8 ${
              showDebtsList ? 'border-l-4 border-danger-500' : 'border-l-4 border-success-500'
            }`}
          >
            <div className="flex items-start">
              {showDebtsList ? (
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="w-6 h-6 text-danger-500" />
                  </div>
                </div>
              ) : (
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center">
                    <FiCheckCircle className="w-6 h-6 text-success-500" />
                  </div>
                </div>
              )}
              <div className="ml-4 flex-1">
                <h3 className="text-xl font-bold text-gray-900">
                  {showCleanName && !showDebtsList ? 'Parabéns! Seu nome está limpo!' : 'Débitos Encontrados'}
                </h3>
                <p className="text-gray-600 mt-1">
                  {hasNegative
                    ? `Encontramos ${debts.length} pendência${debts.length > 1 ? 's' : ''} no seu ${documentLabel}. Veja os detalhes abaixo e descubra como limpar seu nome hoje mesmo.`
                    : 'Não encontramos restrições em seu nome. Continue mantendo suas finanças em dia!'}
                </p>
                {hasNegative && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-danger-100 text-danger-700">
                      <FiAlertTriangle className="w-4 h-4 mr-1" />
                      {finalTotalAmount > 0 ? formatCurrency(finalTotalAmount) : 'Valor não disponível'}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                      <FiClock className="w-4 h-4 mr-1" />
                      Atualizado agora
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Debts List */}
          {showDebtsList && debts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card mb-8"
            >
              <h3 className="text-xl font-semibold mb-6 text-gray-900">Débitos Encontrados</h3>
               
              <div className="space-y-4">
                {debts.map((debt, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className={`flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg ${
                      debt.paid 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-gray-50'
                    }`}
                  >
                    <div className="mb-3 md:mb-0">
                      <div className="flex items-center">
                        <h4 className="font-semibold text-lg text-gray-900">{debt.creditor}</h4>
                        {debt.paid && (
                          <span className="ml-2 bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full font-medium">
                            ✓ Quitado
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">Valor: {formatCurrency(debt.amount)}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      {!debt.paid ? (
                        <>
                          <div className="bg-success-100 text-success-600 px-4 py-2 rounded-lg">
                            <span className="font-bold">{debt.discount}%</span>
                            <span className="text-sm ml-1">desconto</span>
                          </div>
                          <div className="text-gray-500">
                            <span className="text-sm">Pague </span>
                            <span className="font-bold text-success-600">
                              {formatCurrency(debt.amount * (1 - debt.discount / 100))}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-500">
                          <span className="font-bold text-green-600">
                            {formatCurrency(debt.amount * (1 - debt.discount / 100))}
                          </span>
                          <span className="text-sm ml-1"> pago</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div className="mb-4 md:mb-0">
                    <p className="text-gray-600">Total de débitos pendentes:</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(finalTotalAmount)}</p>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-gray-600">Economia média com descontos:</p>
                    <p className="text-2xl font-bold text-success-600">{discountPercentage}%</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Info Box */}
          {showDebtsList && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
            >
              <div className="flex items-start">
                <FiInfo className="w-6 h-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Próximos passos</h4>
                  <ul className="mt-2 space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <FiCheck className="w-4 h-4 text-success-500 mr-2" />
                      Compare as ofertas de desconto de cada débito
                    </li>
                    <li className="flex items-center">
                      <FiCheck className="w-4 h-4 text-success-500 mr-2" />
                      Escolha a melhor opção para seu bolso
                    </li>
                    <li className="flex items-center">
                      <FiCheck className="w-4 h-4 text-success-500 mr-2" />
                      Negocie diretamente pelo nosso site
                    </li>
                    <li className="flex items-center">
                      <FiCheck className="w-4 h-4 text-success-500 mr-2" />
                      Receba confirmação e acompanhe pelo dashboard
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}

          {/* Benefits */}
          {showDebtsList && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
            >
              <div className="card bg-primary-50 border border-primary-200">
                <div className="flex items-center">
                  <FiShield className="w-6 h-6 text-primary-600 mr-3" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Processo 100% Seguro</h4>
                    <p className="text-sm text-gray-600">Seus dados protegidos com criptografia</p>
                  </div>
                </div>
              </div>
              <div className="card bg-primary-50 border border-primary-200">
                <div className="flex items-center">
                  <FiTrendingUp className="w-6 h-6 text-primary-600 mr-3" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Aumente seu Score</h4>
                    <p className="text-sm text-gray-600">Após negociar suas dívidas</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            {showDebtsList ? (
              <div className="card text-center bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Pronto para negociar suas dívidas?
                </h3>
                <p className="text-blue-100 mb-6">
                  Negocie sua dívida diretamente aqui com segurança.
                </p>
                <Link
                  to="/negociar"
                  state={{ debts: debts, document: documentValue, documentType: documentType, totalAmount: finalTotalAmount, totalDiscount }}
                  className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-lg transition-all duration-200 inline-flex items-center space-x-2"
                >
                  <span>Negociar agora</span>
                  <FiArrowRight />
                </Link>
                <p className="text-blue-100 text-sm mt-4">
                  Sem compromisso. Negociação 100% segura.
                </p>
              </div>
            ) : (
              <div className="card text-center">
                <h3 className="text-2xl font-bold mb-4 text-success-600">
                  Continue assim!
                </h3>
                <p className="text-gray-600 mb-6">
                  Seu nome está limpo. Que tal compartilhar com amigos?
                </p>
                <Link
                  to="/"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>Fazer nova consulta</span>
                  <FiArrowRight />
                </Link>
              </div>
            )}
          </motion.div>

          {/* Login Prompt */}
          {!isAuthenticated && showDebtsList && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-6 text-center"
            >
              <p className="text-gray-600">
                Já é cliente?{' '}
                <Link to="/login" className="text-primary-600 hover:underline font-semibold">
                  Entre aqui
                </Link>
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default Result;
